import type { PostLayoutConfig } from '@/lib/layouts/types';
import { parsePaletteColors } from '@/lib/color/parsePaletteColors';
import type { SubtitleTextTransform } from '@/lib/subtitles/subtitleTextTransform';
import { stripDeferredRandomFillers } from '@/lib/layouts/fillerPlaylist';
import { buildPersistedPostLayout } from '@/lib/layouts/persistPostLayout';
import type { LayoutFieldBinds } from '@/lib/layouts/layoutFieldBinds';
import { subtitleOutlineToKit, type SubtitleShadowStyle } from '@/lib/subtitles/subtitleOutline';

/** The editor's subtitle state (camelCase, SubtitleEditorData shape). Typed
 *  structurally here so the pure mapper needs no component import. */
export interface EditorSubtitleState {
  fontFamily: string;
  fontSize: number;
  textColor: string;
  highlightColor: string;
  highlightEnabled: boolean;
  highlightBoxEnabled: boolean;
  highlightBoxBackgroundColor: string;
  highlightBoxBorderRadius: number;
  highlightBoxPaddingX: number;
  highlightBoxPaddingY: number;
  backgroundColor: string;
  backgroundEnabled: boolean;
  backgroundOpacity: number;
  backgroundWidth: number;
  backgroundHeight: number;
  position: string;
  marginVertical: number;
  marginHorizontal: number;
  shadowEnabled: boolean;
  shadowColor: string;
  shadowSize: number;
  strokeEnabled: boolean;
  strokeColor: string;
  strokeWidth: number;
  shadowStyle: SubtitleShadowStyle;
  shadowOffsetX: number;
  shadowOffsetY: number;
  shadowSpread: number;
  animation: string;
  textAlign: string;
  textTransform: SubtitleTextTransform;
  removePunctuation: boolean;
  maxWordsPerLine: number;
}

export interface EditorLogoState {
  url: string | null;
  position: string;
  size: number;
  opacity: number;
  horizontalMargin: number;
  verticalMargin: number;
}

const POSITION_TO_ALIGN: Record<string, string> = {
  'top-center': 'flex-start',
  'middle-center': 'center',
  'bottom-center': 'flex-end',
};

/** Editor state → brand-kit `data` blob (snake_case, the shape
 *  loadBrandKitIntoEditor reads and BrandKitDataInput expects). The kit's
 *  required title/body fonts default to the subtitle font — the editor has
 *  no notion of them. post_layout rides whole ({kind, config}), matching
 *  project.post_layout. */
export function editorDataToKitData({
  subtitle, logo, fontId, postLayout, colors, fieldBinds,
}: {
  subtitle: EditorSubtitleState;
  logo: EditorLogoState;
  fontId: string | null;
  postLayout: PostLayoutConfig | null;
  colors?: string[];
  fieldBinds?: LayoutFieldBinds;
}) {
  const font = { font_id: fontId || subtitle.fontFamily, font_size: subtitle.fontSize };
  const palette = parsePaletteColors(
    colors && colors.length > 0 ? colors : [subtitle.highlightColor],
  );
  const persistedLayout = postLayout
    ? buildPersistedPostLayout({
      postLayout: stripDeferredRandomFillers(postLayout),
      fieldBinds: fieldBinds ?? {},
    })
    : null;
  return {
    colors: palette.length > 0 ? palette : [subtitle.highlightColor],
    title_font: font,
    body_font: font,
    ...(persistedLayout ? { post_layout: persistedLayout } : {}),
    logo: logo.url
      ? {
          logo_image_url: logo.url,
          logo_position: logo.position,
          logo_size: logo.size,
          logo_opacity: logo.opacity,
          logo_horizontal_margin: logo.horizontalMargin,
          logo_vertical_margin: logo.verticalMargin,
        }
      : null,
    subtitle: {
      subtitle_font_id: fontId || subtitle.fontFamily,
      subtitle_font_size: subtitle.fontSize,
      subtitle_color: subtitle.textColor,
      subtitle_highlight_enabled: subtitle.highlightEnabled,
      subtitle_highlight_color: subtitle.highlightColor,
      justify_content: 'center',
      align_items: POSITION_TO_ALIGN[subtitle.position] ?? 'flex-end',
      max_words_per_line: subtitle.maxWordsPerLine,
      vertical_margin_percentage: subtitle.marginVertical,
      horizontal_margin_percentage: subtitle.marginHorizontal,
      animation: subtitle.animation,
      text_align: subtitle.textAlign,
      text_transform: subtitle.textTransform,
      remove_punctuation: subtitle.removePunctuation,
      highlight_box_enabled: subtitle.highlightBoxEnabled,
      highlight_box_background_color: subtitle.highlightBoxBackgroundColor,
      highlight_box_border_radius: subtitle.highlightBoxBorderRadius,
      highlight_box_padding_x: subtitle.highlightBoxPaddingX,
      highlight_box_padding_y: subtitle.highlightBoxPaddingY,
      background_enabled: subtitle.backgroundEnabled,
      background_opacity: subtitle.backgroundOpacity,
      background_color: subtitle.backgroundColor,
      background_height: subtitle.backgroundHeight,
      background_width: subtitle.backgroundWidth,
      shadow_enabled: subtitle.shadowEnabled,
      shadow_color: subtitle.shadowColor,
      shadow_size: subtitle.shadowSize,
      ...subtitleOutlineToKit(subtitle),
    },
  };
}
